<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">提现管理</h1>
            <p class="list-page-description">审核并处理引入人按账单发起的提现申请，跟踪来源账单、分账进度与失败处理结果。</p>
          </div>
          <div class="list-page-heading-actions">
            <button class="btn-secondary inline-flex items-center gap-2" @click="handleTimeoutScan">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              处理超时提现单
            </button>
            <button class="btn-primary inline-flex items-center gap-2" @click="handleCreate">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              按账单代发起
            </button>
          </div>
        </div>
      </div>
    </template>

    <div class="list-page-table-panel mx-6">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="fetchWithdrawalList"
      >
        <el-select v-model="filters.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部状态" :value="undefined" />
          <el-option label="待审核" :value="0" />
          <el-option label="待执行" :value="1" />
          <el-option label="处理中" :value="2" />
          <el-option label="已到账" :value="3" />
          <el-option label="已驳回" :value="4" />
          <el-option label="失败" :value="5" />
        </el-select>

        <el-select v-model="filters.manualHandleStatus" placeholder="请选择处理状态" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部处理状态" :value="undefined" />
          <el-option label="无需处理" :value="0" />
          <el-option label="待人工处理" :value="1" />
          <el-option label="已人工处理" :value="2" />
        </el-select>

        <el-select
          v-model="filters.referrerId"
          placeholder="搜索引入人姓名"
          filterable
          remote
          clearable
          class="form-select form-control-sm filter-bar-field"
          :remote-method="searchReferrers"
          :loading="referrerLoading"
        >
          <el-option
            v-for="referrer in referrerOptions"
            :key="referrer.id"
            :label="referrer.name"
            :value="referrer.id"
          >
            <div class="flex items-center justify-between">
              <span>{{ referrer.name }}</span>
              <span v-if="referrer.phone" class="ml-2 text-xs text-slate-400">{{ referrer.phone }}</span>
            </div>
          </el-option>
        </el-select>

        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          class="filter-bar-field is-wide form-date form-control-sm"
        />

        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th is-center w-10">
                  <input
                    type="checkbox"
                    class="form-check"
                    :checked="allEligibleSelected"
                    :disabled="eligibleRows.length === 0"
                    aria-label="全选待审核提现单"
                    @change="toggleSelectAll"
                  >
                </th>
                <th class="data-table-th text-left">提现单号</th>
                <th class="data-table-th text-left">引入人</th>
                <th class="data-table-th text-right">提现金额</th>
                <th class="data-table-th text-center">来源账单</th>
                <th class="data-table-th text-left">提现状态</th>
                <th class="data-table-th text-left">通道状态</th>
                <th class="data-table-th text-left">人工处理</th>
                <th class="data-table-th text-left">失败信息</th>
                <th class="data-table-th text-left">申请时间</th>
                <th class="data-table-th text-center w-40">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td v-for="m in 11" :key="m" class="data-table-td">
                    <div class="h-4 bg-slate-200 rounded" :class="m === 3 ? 'w-16 ml-auto' : 'w-24'"/>
                  </td>
                </tr>
              </template>
              <tr v-else-if="tableData.length === 0">
                <td colspan="11" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <svg class="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    <span class="text-slate-500">暂无提现记录</span>
                  </div>
                </td>
              </tr>
              <tr v-for="row in tableData" :key="row.id" class="data-table-row">
                <td class="data-table-td is-center">
                  <input
                    v-if="row.status === 0"
                    type="checkbox"
                    class="form-check"
                    :checked="selectedIds.has(row.id)"
                    aria-label="选择该提现单"
                    @change="toggleRowSelection(row)"
                  >
                </td>
                <td class="data-table-td">
                  <span class="font-mono text-slate-900 text-xs">{{ row.withdrawNo }}</span>
                </td>
                <td class="data-table-td">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-medium text-primary-700">
                      {{ row.referrerName?.charAt(0) || 'U' }}
                    </div>
                    <div>
                      <div class="text-sm text-slate-900">{{ row.referrerName || '-' }}</div>
                      <div class="text-xs text-slate-500">ID: {{ row.referrerId || '-' }}</div>
                    </div>
                  </div>
                </td>
                <td class="data-table-td is-right">
                  <span class="money-text">¥{{ formatAmount(row.amount) }}</span>
                </td>
                <td class="data-table-td is-center">
                  <span class="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                    {{ row.sourceDetailCount || row.detailIds?.length || 0 }} 笔
                  </span>
                </td>
                <td class="data-table-td">
                  <span class="status-badge" :class="statusMap[row.status]?.class || statusMap.default.class">
                    <span class="w-1.5 h-1.5 rounded-full mr-1" :class="statusMap[row.status]?.dot || statusMap.default.dot"/>
                    {{ row.statusName || statusMap[row.status]?.label || '未知' }}
                  </span>
                </td>
                <td class="data-table-td">
                  <div class="text-sm text-slate-900">{{ row.channelStatusName || channelStatusMap[row.channelStatus] || row.channelStatus || '-' }}</div>
                </td>
                <td class="data-table-td">
                  <span class="status-badge" :class="manualHandleMap[row.manualHandleStatus]?.class || manualHandleMap.default.class">
                    {{ row.manualHandleStatusName || manualHandleMap[row.manualHandleStatus]?.label || '无需处理' }}
                  </span>
                </td>
                <td class="data-table-td">
                  <div v-if="row.latestFailStage || row.latestFailReason" class="max-w-[220px]">
                    <div class="text-xs text-red-600">{{ row.latestFailStage || '失败' }}</div>
                    <div class="text-xs text-slate-500 truncate" :title="row.latestFailReason">{{ row.latestFailReason || '-' }}</div>
                  </div>
                  <span v-else class="text-slate-400">-</span>
                </td>
                <td class="data-table-td text-slate-600 text-xs">{{ row.createTime || '-' }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button v-if="row.status === 0" class="table-action-button is-success" title="通过" aria-label="通过" @click="handleApprove(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </button>
                    <button v-if="row.status === 0" class="table-action-button is-danger" title="拒绝" aria-label="拒绝" @click="handleReject(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                    <button v-if="row.manualHandleStatus === 1" class="table-action-button is-warning" title="完成人工处理" aria-label="完成人工处理" @click="openManualDialog(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </button>
                    <button class="table-action-button is-primary" title="查看详情" aria-label="查看详情" @click="handleViewDetail(row)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        class="pagination-bridge"
        :pagination="pagination"
        :total="total"
        :page-sizes="[10, 20, 50]"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />

      <div
        v-if="selectedCount > 0"
        class="sticky bottom-0 z-20 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-white px-4 py-3 shadow-lg"
      >
        <div class="flex items-center gap-3 text-sm">
          <span class="font-medium text-slate-900">已选 {{ selectedCount }} 条待审核</span>
          <span class="text-slate-500">合计 <span class="money-text">¥{{ formatAmount(selectedTotalAmount) }}</span></span>
        </div>
        <div class="flex gap-2">
          <button type="button" class="btn-secondary" :disabled="batchRunning" @click="clearSelection">清空</button>
          <button type="button" class="btn-danger-secondary" :disabled="batchRunning" @click="openBatchReject">批量驳回</button>
          <button type="button" class="btn-primary" :disabled="batchRunning" @click="handleBatchApprove">
            {{ batchRunning ? '处理中...' : '批量通过' }}
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="createDialogVisible" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="createDialogVisible = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">按账单代发起提现</h3>
                <p class="text-sm text-slate-500">先选择引入人，再勾选可提现账单后提交；提现金额由所选账单合计得出，无需手动填写。</p>
              </div>
              <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="createDialogVisible = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="flex-1 overflow-y-auto min-h-0 p-6 space-y-5">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label class="form-label">引入人 <span class="text-red-500">*</span></label>
                  <el-select
                    v-model="createForm.referrerId"
                    placeholder="搜索引入人姓名"
                    filterable
                    remote
                    clearable
                    class="form-select w-full"
                    :remote-method="searchReferrers"
                    :loading="referrerLoading"
                  >
                    <el-option
                      v-for="referrer in referrerOptions"
                      :key="referrer.id"
                      :label="referrer.name"
                      :value="referrer.id"
                    >
                      <div class="flex items-center justify-between">
                        <span>{{ referrer.name }}</span>
                        <span v-if="referrer.phone" class="ml-2 text-xs text-slate-400">{{ referrer.phone }}</span>
                      </div>
                    </el-option>
                  </el-select>
                </div>
                <div>
                  <label class="form-label">账单关键词</label>
                  <input v-model="createForm.keyword" type="text" placeholder="账单号/订单号/客户" class="form-input w-full">
                </div>
                <div class="flex items-end">
                  <button class="btn-secondary w-full" @click="fetchAvailableDetails(true)">搜索可提现账单</button>
                </div>
                <div class="bg-primary-50 rounded-xl p-4">
                  <div class="text-xs text-primary-700 mb-1">已选提现合计</div>
                  <div class="text-2xl font-semibold text-primary-700">¥{{ formatAmount(selectedCreateAmount) }}</div>
                  <div class="text-xs text-primary-600 mt-1">{{ selectedCreateIds.length }} 笔账单</div>
                </div>
              </div>

              <div class="border border-slate-200 rounded-xl overflow-hidden">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left w-10">
                        <input type="checkbox" class="form-check" :checked="allAvailableSelected" @change="toggleAllAvailable">
                      </th>
                      <th class="data-table-th text-left">账单</th>
                      <th class="data-table-th text-left">订单/客户</th>
                      <th class="data-table-th text-right">佣金毛额</th>
                      <th class="data-table-th text-right">税费</th>
                      <th class="data-table-th text-right">可提现</th>
                      <th class="data-table-th text-left">结算时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="availableLoading">
                      <td colspan="7" class="data-table-empty">加载中...</td>
                    </tr>
                    <tr v-else-if="availableDetails.length === 0">
                      <td colspan="7" class="data-table-empty">暂无可提现账单</td>
                    </tr>
                    <tr v-for="item in availableDetails" :key="item.commissionDetailId" class="data-table-row">
                      <td class="data-table-td is-center">
                        <input type="checkbox" class="form-check" :checked="isCreateSelected(item.commissionDetailId)" @change="toggleAvailableDetail(item)">
                      </td>
                      <td class="data-table-td">
                        <div class="font-mono text-xs text-slate-900">{{ item.billingNo || item.billingId || '-' }}</div>
                        <div class="text-xs text-slate-500">明细ID: {{ item.commissionDetailId }}</div>
                      </td>
                      <td class="data-table-td">
                        <div class="text-sm text-slate-900">{{ item.orderNo || item.orderId || '-' }}</div>
                        <div class="text-xs text-slate-500">{{ item.customerName || '-' }}</div>
                      </td>
                      <td class="data-table-td is-right">¥{{ formatAmount(item.commissionAmount) }}</td>
                      <td class="data-table-td is-right text-slate-600">¥{{ formatAmount(item.taxAmount) }}</td>
                      <td class="data-table-td is-right money-text">¥{{ formatAmount(item.withdrawableAmount) }}</td>
                      <td class="data-table-td text-xs text-slate-600">{{ item.settleTime || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="flex items-center justify-between">
                <button class="btn-secondary" :disabled="availablePagination.current <= 1" @click="changeAvailablePage(availablePagination.current - 1)">上一页</button>
                <span class="text-sm text-slate-500">第 {{ availablePagination.current }} 页，共 {{ availableTotal }} 条</span>
                <button class="btn-secondary" :disabled="availableDetails.length >= availableTotal" @click="changeAvailablePage(availablePagination.current + 1)">下一页</button>
              </div>

              <div>
                <label class="form-label">备注</label>
                <textarea v-model="createForm.remark" rows="3" placeholder="可选填写备注信息" class="form-textarea w-full resize-none"></textarea>
              </div>
            </div>

            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button class="btn-secondary" @click="createDialogVisible = false">取消</button>
              <button class="btn-primary" :disabled="!canSubmitCreate" @click="handleSubmitCreate">提交提现申请</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="detailDialogVisible" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="detailDialogVisible = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">提现详情</h3>
                <p class="text-sm text-slate-500 font-mono">{{ currentRow?.withdrawNo }}</p>
              </div>
              <div class="flex items-center gap-3">
                <span v-if="currentRow" class="status-badge" :class="statusMap[currentRow.status]?.class || statusMap.default.class">
                  {{ currentRow.statusName || statusMap[currentRow.status]?.label }}
                </span>
                <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="detailDialogVisible = false">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="flex-1 overflow-y-auto min-h-0 p-6 space-y-5">
              <div v-if="detailLoading" class="py-16 text-center text-slate-500">加载详情中...</div>
              <template v-else>
                <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div class="bg-slate-50 rounded-xl p-4">
                    <div class="text-xs text-slate-500 mb-1">引入人</div>
                    <div class="text-sm font-medium text-slate-900">{{ currentRow?.referrerName || '-' }}</div>
                    <div class="text-xs text-slate-500">ID: {{ currentRow?.referrerId || '-' }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-4">
                    <div class="text-xs text-slate-500 mb-1">提现金额</div>
                    <div class="text-lg money-text">¥{{ formatAmount(currentRow?.amount) }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-4">
                    <div class="text-xs text-slate-500 mb-1">实际到账</div>
                    <div class="text-lg font-semibold text-emerald-600">¥{{ formatAmount(currentRow?.actualAmount) }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-4">
                    <div class="text-xs text-slate-500 mb-1">通道状态</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.channelStatusName || currentRow?.channelStatus || '-' }}</div>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-4">
                    <div class="text-xs text-slate-500 mb-1">人工处理</div>
                    <div class="text-sm text-slate-900">{{ currentRow?.manualHandleStatusName || manualHandleMap[currentRow?.manualHandleStatus]?.label || '-' }}</div>
                  </div>
                </div>

                <section class="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div class="px-4 py-3 border-b border-slate-200">
                    <div class="flex items-center justify-between mb-3">
                      <div>
                        <h4 class="text-sm font-semibold text-slate-900">账单处理明细</h4>
                        <p class="text-xs text-slate-500 mt-1">每笔账单独立展示分账结果与提现结果，整体进度仅作辅助参考。</p>
                      </div>
                      <span class="text-xs text-slate-500">{{ currentRow?.sourceDetails?.length || 0 }} 笔</span>
                    </div>
                    <div class="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div v-for="item in sourceStatusSummary" :key="item.label" class="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div class="text-xs text-slate-500">{{ item.label }}</div>
                        <div class="text-lg font-semibold mt-0.5" :class="item.class">{{ item.count }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="overflow-x-auto">
                    <table class="data-table text-sm">
                      <thead class="data-table-head">
                        <tr>
                          <th class="data-table-th text-left">账单/订单</th>
                          <th class="data-table-th text-left">客户</th>
                          <th class="data-table-th text-right">佣金毛额</th>
                          <th class="data-table-th text-right">税费</th>
                          <th class="data-table-th text-right">提现金额</th>
                          <th class="data-table-th text-left min-w-[240px]">账单进度</th>
                          <th class="data-table-th text-left">分账状态</th>
                          <th class="data-table-th text-left">分账单号</th>
                          <th class="data-table-th text-left">提现状态</th>
                          <th class="data-table-th text-left">失败信息</th>
                          <th class="data-table-th text-left">更新时间</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!currentRow?.sourceDetails?.length">
                          <td colspan="11" class="data-table-empty">暂无来源账单明细</td>
                        </tr>
                        <tr v-for="detail in currentRow.sourceDetails" :key="detail.withdrawDetailId" class="data-table-row">
                          <td class="data-table-td">
                            <div class="font-mono text-xs text-slate-900">{{ detail.billingNo || detail.billingId || '-' }}</div>
                            <div class="text-xs text-slate-500">{{ detail.orderNo || detail.orderId || '-' }}</div>
                          </td>
                          <td class="data-table-td">{{ detail.customerName || '-' }}</td>
                          <td class="data-table-td is-right">¥{{ formatAmount(detail.commissionAmount) }}</td>
                          <td class="data-table-td is-right">¥{{ formatAmount(detail.taxAmount) }}</td>
                          <td class="data-table-td is-right money-text">¥{{ formatAmount(detail.amount) }}</td>
                          <td class="data-table-td">
                            <div class="flex items-center gap-1.5 min-w-[220px]">
                              <template v-for="(step, index) in getBillProgressSteps(detail)" :key="step.key">
                                <div class="flex flex-col items-center gap-1">
                                  <div
                                    class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border"
                                    :class="getBillProgressStepClass(step.status)"
                                    :title="step.tip"
                                  >
                                    {{ index + 1 }}
                                  </div>
                                  <div class="text-xs leading-none whitespace-nowrap" :class="getBillProgressTextClass(step.status)">
                                    {{ step.label }}
                                  </div>
                                </div>
                                <div
                                  v-if="index < getBillProgressSteps(detail).length - 1"
                                  class="h-px w-5"
                                  :class="step.status === 'failed' ? 'bg-red-300' : 'bg-slate-200'"
                                ></div>
                              </template>
                            </div>
                          </td>
                          <td class="data-table-td">{{ detail.separateStatusName || '-' }}</td>
                          <td class="data-table-td">
                            <div class="font-mono text-xs text-slate-900">{{ detail.separateNo || '-' }}</div>
                            <div class="font-mono text-xs text-slate-500">{{ detail.outSeparateNo || detail.separateOrderId || '-' }}</div>
                          </td>
                          <td class="data-table-td">{{ displayWithdrawStatusName(detail) }}</td>
                          <td class="data-table-td">
                            <div v-if="detail.failStage || detail.failReason" class="max-w-[240px]">
                              <div class="text-xs text-red-600">{{ detail.failStageName || detail.failStage }}</div>
                              <div class="text-xs text-slate-500 truncate" :title="detail.failReason">{{ detail.failReason || '-' }}</div>
                            </div>
                            <span v-else class="text-slate-400">-</span>
                          </td>
                          <td class="data-table-td text-xs text-slate-600">{{ detail.updateTime || detail.createTime || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <section class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div class="bg-white border border-slate-200 rounded-xl p-4">
                    <div class="mb-4">
                      <h4 class="text-sm font-semibold text-slate-900">提现单整体进度</h4>
                      <p class="text-xs text-slate-500 mt-1">
                        账单级分账和提现结果以上方账单处理明细为准。
                      </p>
                    </div>
                    <div v-if="!currentRow?.progress?.length" class="text-sm text-slate-500">暂无整体进度</div>
                    <div v-for="node in currentRow.progress" :key="node.nodeCode" class="flex gap-3 pb-4 last:pb-0">
                      <div class="mt-1 w-2.5 h-2.5 rounded-full" :class="getProgressDotClass(node.status)"></div>
                      <div>
                        <div class="text-sm font-medium text-slate-900">{{ node.nodeName }}</div>
                        <div class="text-xs text-slate-500">{{ node.description || '-' }}</div>
                        <div class="text-xs text-slate-400 mt-1">{{ node.time || '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="bg-white border border-slate-200 rounded-xl p-4">
                    <h4 class="text-sm font-semibold text-slate-900 mb-4">资金流水</h4>
                    <div v-if="!currentRow?.fundFlows?.length" class="text-sm text-slate-500">暂无资金流水</div>
                    <div v-for="flow in currentRow.fundFlows" :key="flow.id || `${flow.bizType}-${flow.createTime}`" class="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-b-0">
                      <div>
                        <div class="text-sm text-slate-900">{{ flow.actionTypeName || flow.actionType || flow.flowTypeName || '资金变动' }}</div>
                        <div class="text-xs text-slate-500">{{ flow.bizNo || flow.remark || '-' }}</div>
                      </div>
                      <div class="text-right">
                        <div class="text-sm font-medium text-slate-900">¥{{ formatAmount(flow.changeAmount || flow.amount) }}</div>
                        <div class="text-xs text-slate-400">{{ flow.createTime || '-' }}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section v-if="currentRow?.manualHandleRemark || currentRow?.rejectReason || currentRow?.auditRemark" class="bg-slate-50 rounded-xl p-4">
                  <h4 class="text-sm font-semibold text-slate-900 mb-3">处理备注</h4>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div v-if="currentRow?.auditRemark">
                      <div class="text-xs text-slate-500 mb-1">审核备注</div>
                      <div class="text-slate-900">{{ currentRow.auditRemark }}</div>
                    </div>
                    <div v-if="currentRow?.rejectReason">
                      <div class="text-xs text-slate-500 mb-1">驳回原因</div>
                      <div class="text-red-600">{{ currentRow.rejectReason }}</div>
                    </div>
                    <div v-if="currentRow?.manualHandleRemark">
                      <div class="text-xs text-slate-500 mb-1">人工处理备注</div>
                      <div class="text-slate-900">{{ currentRow.manualHandleRemark }}</div>
                    </div>
                  </div>
                </section>
              </template>
            </div>

            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button v-if="currentRow?.manualHandleStatus === 1" class="btn-secondary" @click="openManualDialog(currentRow)">完成人工处理</button>
              <button v-if="currentRow?.status === 0" class="btn-danger-secondary" @click="showRejectInput = true">拒绝</button>
              <button v-if="currentRow?.status === 0" class="btn-primary" @click="handleApproveFromDialog">通过</button>
              <button class="btn-secondary" @click="detailDialogVisible = false">关闭</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showRejectInput" class="fixed inset-0 z-[60] overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="showRejectInput = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 class="text-lg font-semibold text-slate-900">拒绝原因</h3>
              <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="showRejectInput = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div class="p-6">
              <textarea v-model="rejectReason" rows="4" placeholder="请输入拒绝原因" class="form-textarea w-full resize-none"></textarea>
            </div>
            <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button class="btn-secondary" @click="showRejectInput = false">取消</button>
              <button class="btn-danger-secondary" @click="confirmReject">确认拒绝</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 批量驳回弹窗 -->
    <Teleport to="body">
      <div v-if="batchRejectVisible" class="fixed inset-0 z-[60] overflow-y-auto" @keydown.esc="batchRejectVisible = false">
        <div class="fixed inset-0 bg-black/50" @click="batchRejectVisible = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full" role="dialog" aria-modal="true" aria-labelledby="batch-reject-title">
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 id="batch-reject-title" class="text-lg font-semibold text-slate-900">批量驳回</h3>
              <button class="p-1.5 text-slate-400 hover:text-slate-600" aria-label="关闭" @click="batchRejectVisible = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div class="p-6">
              <p class="mb-3 text-sm text-slate-500">以下驳回原因将应用于所选 {{ selectedCount }} 条提现申请。</p>
              <label for="batch-reject-reason" class="form-label">驳回原因 <span class="text-red-500">*</span></label>
              <textarea id="batch-reject-reason" v-model="batchRejectReason" rows="4" placeholder="请输入驳回原因" class="form-textarea w-full resize-none"></textarea>
            </div>
            <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button class="btn-secondary" :disabled="batchRunning" @click="batchRejectVisible = false">取消</button>
              <button class="btn-danger-secondary" :disabled="batchRunning" @click="confirmBatchReject">
                {{ batchRunning ? '处理中...' : '确认驳回' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="manualDialogVisible" class="fixed inset-0 z-[60] overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="manualDialogVisible = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">完成人工处理</h3>
                <p class="text-sm text-slate-500 font-mono">{{ manualRow?.withdrawNo }}</p>
              </div>
              <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="manualDialogVisible = false">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div class="p-6 space-y-4">
              <div class="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 space-y-1">
                <div v-for="tip in manualRiskTips" :key="tip">{{ tip }}</div>
              </div>
              <textarea v-model="manualRemark" rows="4" placeholder="请输入人工处理说明" class="form-textarea w-full resize-none"></textarea>
            </div>
            <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button class="btn-secondary" @click="manualDialogVisible = false">取消</button>
              <button class="btn-primary" @click="handleManualComplete">确认完成</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import {
  getWithdrawOrderList,
  getWithdrawOrderFullDetail,
  listAvailableWithdrawDetails,
  createWithdrawByDetails,
  auditWithdraw,
  completeManualWithdraw,
  handleWithdrawTimeoutOrders
} from '@/api/commission'
import { getReferrerPage } from '@/api/referrer'

const statusMap = {
  0: { label: '待审核', class: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  1: { label: '待执行', class: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  2: { label: '处理中', class: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  3: { label: '已到账', class: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  4: { label: '已驳回', class: 'bg-slate-100 text-slate-700', dot: 'bg-slate-500' },
  5: { label: '失败', class: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  default: { label: '未知', class: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' }
}

const manualHandleMap = {
  0: { label: '无需处理', class: 'bg-slate-100 text-slate-600' },
  1: { label: '待人工处理', class: 'bg-red-100 text-red-700' },
  2: { label: '已人工处理', class: 'bg-emerald-100 text-emerald-700' },
  default: { label: '无需处理', class: 'bg-slate-100 text-slate-600' }
}

const channelStatusMap = {
  PENDING_AUDIT: '待审核',
  WAIT_EXECUTE: '待执行',
  'SEPARATE.PROCESSING': '分账处理中',
  'SEPARATE.FAILED': '分账失败',
  'WITHDRAW.PROCESSING': '提现处理中',
  'WITHDRAW.FAILED': '提现失败',
  SUCCESS: '成功'
}

const filters = reactive({
  status: undefined,
  manualHandleStatus: undefined,
  referrerId: undefined,
  dateRange: null
})

const referrerOptions = ref([])
const referrerLoading = ref(false)

async function searchReferrers(keyword) {
  referrerLoading.value = true
  try {
    const data = await getReferrerPage({
      name: keyword || undefined,
      status: 1,
      page: 1,
      size: 20
    })
    referrerOptions.value = data.records || []
  } catch {
    referrerOptions.value = []
  } finally {
    referrerLoading.value = false
  }
}

const tableData = ref([])
const loading = ref(false)
const total = ref(0)
const pagination = reactive({
  current: 1,
  size: 10,
  pages: 1
})

const createDialogVisible = ref(false)
const availableLoading = ref(false)
const availableDetails = ref([])
const availableTotal = ref(0)
const availablePagination = reactive({
  current: 1,
  size: 10
})
const createForm = reactive({
  referrerId: '',
  keyword: '',
  selectedDetails: {},
  remark: '',
  requireAudit: true
})

const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const currentRow = ref(null)
const showRejectInput = ref(false)
const rejectReason = ref('')

const selectedIds = ref(new Set())
const batchRunning = ref(false)
const batchRejectVisible = ref(false)
const batchRejectReason = ref('')

const eligibleRows = computed(() => tableData.value.filter(row => row.status === 0))
const selectedCount = computed(() => selectedIds.value.size)
const allEligibleSelected = computed(() =>
  eligibleRows.value.length > 0 && eligibleRows.value.every(row => selectedIds.value.has(row.id))
)
const selectedTotalAmount = computed(() =>
  tableData.value
    .filter(row => selectedIds.value.has(row.id))
    .reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
)

function toggleRowSelection(row) {
  const next = new Set(selectedIds.value)
  if (next.has(row.id)) next.delete(row.id)
  else next.add(row.id)
  selectedIds.value = next
}

function toggleSelectAll() {
  selectedIds.value = allEligibleSelected.value
    ? new Set()
    : new Set(eligibleRows.value.map(row => row.id))
}

function clearSelection() {
  selectedIds.value = new Set()
}

function pruneSelection() {
  const eligible = new Set(eligibleRows.value.map(row => row.id))
  selectedIds.value = new Set([...selectedIds.value].filter(id => eligible.has(id)))
}

const manualDialogVisible = ref(false)
const manualRow = ref(null)
const manualRemark = ref('')

const selectedCreateList = computed(() => Object.values(createForm.selectedDetails))
const selectedCreateIds = computed(() => selectedCreateList.value.map(item => item.commissionDetailId))
const selectedCreateAmount = computed(() => selectedCreateList.value.reduce((sum, item) => sum + toNumber(item.withdrawableAmount), 0))
const canSubmitCreate = computed(() => Number(createForm.referrerId) > 0 && selectedCreateIds.value.length > 0 && selectedCreateAmount.value > 0)
const allAvailableSelected = computed(() => availableDetails.value.length > 0 && availableDetails.value.every(item => isCreateSelected(item.commissionDetailId)))
const sourceStatusSummary = computed(() => {
  const details = currentRow.value?.sourceDetails || []
  return [
    { label: '账单总数', count: details.length, class: 'text-slate-900' },
    { label: '分账成功', count: details.filter(item => item.separateStatus === 2).length, class: 'text-emerald-600' },
    { label: '分账失败', count: details.filter(item => item.separateStatus === 3).length, class: 'text-red-600' },
    { label: '提现成功', count: details.filter(item => item.withdrawStatus === 3).length, class: 'text-emerald-600' },
    { label: '提现失败', count: details.filter(item => item.withdrawStatus === 4).length, class: 'text-red-600' },
    { label: '待处理', count: details.filter(item => [0, 1, 2].includes(item.withdrawStatus)).length, class: 'text-amber-600' }
  ]
})
const manualRiskTips = computed(() => {
  const details = manualRow.value?.sourceDetails || currentRow.value?.sourceDetails || []
  const tips = []
  if (details.some(item => item.separateStatus === 3)) {
    tips.push('存在分账失败账单：请优先确认是否需要重试分账或释放账单占用，不建议直接标记提现完成。')
  }
  if (details.some(item => item.separateStatus === 2 && item.withdrawStatus === 4)) {
    tips.push('存在分账成功但提现失败账单：请确认是否已线下打款，避免重复出款。')
  }
  if (details.some(item => item.withdrawStatus === 3) && details.some(item => item.withdrawStatus !== 3)) {
    tips.push('存在部分提现成功账单：人工完成前请核对已到账明细和未完成明细。')
  }
  if (!tips.length) {
    tips.push('请先确认失败阶段和线下处理结果，再完成人工处理。')
  }
  return tips
})

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function formatAmount(value) {
  return toNumber(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function displayWithdrawStatusName(detail) {
  if (detail.withdrawStatus === 0 || detail.withdrawStatusName === '已占用') {
    return '已锁定'
  }
  return detail.withdrawStatusName || '-'
}

function getBillProgressSteps(detail) {
  return [
    {
      key: 'locked',
      label: '锁定',
      status: detail.withdrawStatus === 5 ? 'skipped' : 'done',
      tip: '账单已被当前提现单锁定'
    },
    {
      key: 'separate',
      label: '分账',
      status: resolveSeparateProgressStatus(detail),
      tip: detail.failStage === 'SEPARATE_SUBMIT' || detail.failStage === 'SEPARATE_RESULT'
        ? detail.failReason || '分账失败'
        : detail.separateStatusName || '分账状态'
    },
    {
      key: 'withdraw',
      label: '提现',
      status: resolveWithdrawProgressStatus(detail),
      tip: detail.failStage === 'WITHDRAW_SUBMIT' || detail.failStage === 'WITHDRAW_RESULT'
        ? detail.failReason || '提现失败'
        : displayWithdrawStatusName(detail)
    },
    {
      key: 'finish',
      label: '完成',
      status: detail.withdrawStatus === 3 ? 'done' : detail.withdrawStatus === 4 ? 'failed' : 'pending',
      tip: detail.withdrawStatus === 3 ? '提现成功' : detail.withdrawStatus === 4 ? detail.failReason || '提现失败' : '等待完成'
    }
  ]
}

function resolveSeparateProgressStatus(detail) {
  if (detail.separateStatus === 2 || detail.separateStatus === 4) return 'done'
  if (detail.separateStatus === 3) return 'failed'
  if (detail.separateStatus === 1) return 'processing'
  return 'pending'
}

function resolveWithdrawProgressStatus(detail) {
  if (detail.withdrawStatus === 3) return 'done'
  if (detail.withdrawStatus === 4) return 'failed'
  if (detail.withdrawStatus === 2) return 'processing'
  if (detail.withdrawStatus === 1) return 'pending'
  return 'pending'
}

function getBillProgressStepClass(status) {
  const map = {
    done: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    processing: 'bg-blue-50 text-blue-700 border-blue-300',
    failed: 'bg-red-50 text-red-700 border-red-300',
    skipped: 'bg-slate-50 text-slate-500 border-slate-300',
    pending: 'bg-slate-50 text-slate-500 border-slate-200'
  }
  return map[status] || map.pending
}

function getBillProgressTextClass(status) {
  const map = {
    done: 'text-emerald-700',
    processing: 'text-blue-700',
    failed: 'text-red-700',
    skipped: 'text-slate-500',
    pending: 'text-slate-500'
  }
  return map[status] || map.pending
}

function toDateTimeStart(date) {
  return date ? `${date} 00:00:00` : undefined
}

function toDateTimeEnd(date) {
  return date ? `${date} 23:59:59` : undefined
}

async function fetchWithdrawalList() {
  loading.value = true
  try {
    const params = {
      pageNum: pagination.current,
      pageSize: pagination.size,
      referrerId: filters.referrerId ? Number(filters.referrerId) : undefined,
      status: filters.status,
      manualHandleStatus: filters.manualHandleStatus,
      startDate: toDateTimeStart(filters.dateRange?.[0]),
      endDate: toDateTimeEnd(filters.dateRange?.[1])
    }
    const res = await getWithdrawOrderList(params)
    tableData.value = res.records || []
    total.value = res.total || 0
    pagination.pages = res.pages || Math.max(1, Math.ceil(total.value / pagination.size))
    pruneSelection()
  } catch (error) {
    ElMessage.error('获取提现列表失败')
    console.error(error)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pagination.current = 1
  fetchWithdrawalList()
}

function handleReset() {
  filters.status = undefined
  filters.manualHandleStatus = undefined
  filters.referrerId = undefined
  filters.dateRange = null
  handleSearch()
}

function handlePageChange(page) {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  fetchWithdrawalList()
}

function handlePageSizeChange(size) {
  pagination.size = size
  pagination.current = 1
  fetchWithdrawalList()
}

function handleCreate() {
  createForm.referrerId = ''
  createForm.keyword = ''
  createForm.selectedDetails = {}
  createForm.remark = ''
  createForm.requireAudit = true
  availableDetails.value = []
  availableTotal.value = 0
  availablePagination.current = 1
  createDialogVisible.value = true
}

async function fetchAvailableDetails(reset = false) {
  if (!Number(createForm.referrerId)) {
    ElMessage.warning('请先选择引入人')
    return
  }
  if (reset) {
    availablePagination.current = 1
    createForm.selectedDetails = {}
  }
  availableLoading.value = true
  try {
    const res = await listAvailableWithdrawDetails({
      referrerId: Number(createForm.referrerId),
      keyword: createForm.keyword || undefined,
      pageNum: availablePagination.current,
      pageSize: availablePagination.size
    })
    availableDetails.value = res.records || []
    availableTotal.value = res.total || 0
  } catch (error) {
    ElMessage.error('查询可提现账单失败')
    console.error(error)
  } finally {
    availableLoading.value = false
  }
}

function changeAvailablePage(page) {
  if (page < 1) return
  availablePagination.current = page
  fetchAvailableDetails()
}

function isCreateSelected(id) {
  return !!createForm.selectedDetails[id]
}

function toggleAvailableDetail(item) {
  const id = item.commissionDetailId
  if (!id) return
  const next = { ...createForm.selectedDetails }
  if (next[id]) {
    delete next[id]
  } else {
    next[id] = item
  }
  createForm.selectedDetails = next
}

function toggleAllAvailable(event) {
  const next = { ...createForm.selectedDetails }
  if (event.target.checked) {
    availableDetails.value.forEach(item => {
      if (item.commissionDetailId) next[item.commissionDetailId] = item
    })
  } else {
    availableDetails.value.forEach(item => {
      delete next[item.commissionDetailId]
    })
  }
  createForm.selectedDetails = next
}

async function handleSubmitCreate() {
  if (!canSubmitCreate.value) return
  try {
    await createWithdrawByDetails({
      referrerId: Number(createForm.referrerId),
      commissionDetailIds: selectedCreateIds.value,
      expectedAmount: Number(selectedCreateAmount.value.toFixed(2)),
      remark: createForm.remark,
      requireAudit: createForm.requireAudit
    })
    ElMessage.success('提现申请已提交')
    createDialogVisible.value = false
    fetchWithdrawalList()
  } catch (error) {
    ElMessage.error('提交失败，请重试')
    console.error(error)
  }
}

async function handleViewDetail(row) {
  detailDialogVisible.value = true
  detailLoading.value = true
  currentRow.value = { ...row, sourceDetails: [], progress: [], fundFlows: [] }
  rejectReason.value = ''
  showRejectInput.value = false
  try {
    const res = await getWithdrawOrderFullDetail(row.id)
    currentRow.value = {
      ...row,
      ...res,
      sourceDetails: res?.sourceDetails || [],
      progress: res?.progress || [],
      fundFlows: res?.fundFlows || []
    }
  } catch (error) {
    ElMessage.error('获取提现详情失败')
    console.error(error)
  } finally {
    detailLoading.value = false
  }
}

async function handleApprove(row) {
  try {
    await ElMessageBox.confirm(`确认通过引入人“${row.referrerName || row.referrerId}”的提现申请吗？`, '审核确认', {
      confirmButtonText: '确认通过',
      cancelButtonText: '取消',
      type: 'success'
    })
    await auditWithdraw({ withdrawOrderId: row.id, action: 'APPROVE' })
    ElMessage.success('已通过')
    fetchWithdrawalList()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('操作失败')
  }
}

async function handleApproveFromDialog() {
  if (!currentRow.value) return
  await handleApprove(currentRow.value)
  detailDialogVisible.value = false
}

async function handleReject(row) {
  currentRow.value = row
  rejectReason.value = ''
  showRejectInput.value = true
}

async function confirmReject() {
  if (!currentRow.value) return
  try {
    await auditWithdraw({
      withdrawOrderId: currentRow.value.id,
      action: 'REJECT',
      rejectReason: rejectReason.value
    })
    ElMessage.success('已拒绝')
    showRejectInput.value = false
    detailDialogVisible.value = false
    fetchWithdrawalList()
  } catch (error) {
    ElMessage.error('操作失败')
    console.error(error)
  }
}

async function runBatchAudit(action, rejectReasonText) {
  const ids = [...selectedIds.value]
  batchRunning.value = true
  let success = 0
  let failed = 0
  for (const id of ids) {
    try {
      await auditWithdraw({ withdrawOrderId: id, action, rejectReason: rejectReasonText })
      success += 1
    } catch (error) {
      failed += 1
      console.error(`批量审核失败，提现单 ${id}:`, error)
    }
  }
  batchRunning.value = false
  const actionLabel = action === 'APPROVE' ? '通过' : '驳回'
  if (failed === 0) {
    ElMessage.success(`批量${actionLabel}完成：成功 ${success} 条`)
  } else {
    ElMessage.warning(`批量${actionLabel}完成：成功 ${success} 条，失败 ${failed} 条，请核对后重试`)
  }
  clearSelection()
  fetchWithdrawalList()
}

async function handleBatchApprove() {
  if (selectedCount.value === 0) return
  try {
    await ElMessageBox.confirm(
      `确认通过所选 ${selectedCount.value} 条提现申请，合计 ¥${formatAmount(selectedTotalAmount.value)} 吗？`,
      '批量审核确认',
      { confirmButtonText: '确认通过', cancelButtonText: '取消', type: 'success' }
    )
  } catch {
    return
  }
  await runBatchAudit('APPROVE')
}

function openBatchReject() {
  batchRejectReason.value = ''
  batchRejectVisible.value = true
}

async function confirmBatchReject() {
  const reason = batchRejectReason.value.trim()
  if (!reason) {
    ElMessage.warning('请填写驳回原因')
    return
  }
  batchRejectVisible.value = false
  await runBatchAudit('REJECT', reason)
}

function openManualDialog(row) {
  manualRow.value = row
  manualRemark.value = row.manualHandleRemark || ''
  manualDialogVisible.value = true
}

async function handleManualComplete() {
  if (!manualRow.value) return
  try {
    await completeManualWithdraw({
      withdrawOrderId: manualRow.value.id,
      remark: manualRemark.value
    })
    ElMessage.success('人工处理已完成')
    manualDialogVisible.value = false
    detailDialogVisible.value = false
    fetchWithdrawalList()
  } catch (error) {
    ElMessage.error('操作失败')
    console.error(error)
  }
}

async function handleTimeoutScan() {
  try {
    await ElMessageBox.confirm('确认触发提现超时处理？系统会扫描并处理符合条件的超时订单。', '处理超时单', {
      confirmButtonText: '确认处理',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await handleWithdrawTimeoutOrders()
    ElMessage.success('已触发超时处理')
    fetchWithdrawalList()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error('操作失败')
  }
}

function getProgressDotClass(status) {
  const map = {
    COMPLETED: 'bg-emerald-500',
    FAILED: 'bg-red-500',
    PENDING: 'bg-amber-500',
    SKIPPED: 'bg-slate-300'
  }
  return map[status] || 'bg-slate-300'
}

onMounted(() => {
  fetchWithdrawalList()
})
</script>
